/**
 * Header section: --header-height sync and dual logo display swap.
 * Expects a single `.shopify-section--header` in the DOM.
 */
(function () {
  'use strict';

  function setHeaderHeight(root) {
    if (!root) return;
    document.documentElement.style.setProperty('--header-height', root.clientHeight.toFixed(2) + 'px');
  }

  function initHeaderTopBarCarousel(root) {
    var bar = root.querySelector('[data-header-top-bar]');
    if (!bar || bar.dataset.headerTopBarCarouselInit === 'true') return;
    bar.dataset.headerTopBarCarouselInit = 'true';

    var viewport = bar.querySelector('.header__top-bar-viewport');
    var track = bar.querySelector('[data-header-top-bar-track]');
    var originals = track ? track.querySelectorAll('.header__top-bar-slide') : [];
    if (!viewport || !track || originals.length === 0) return;

    var n = originals.length;
    var loop = n >= 2;
    var slideWidth = 0;
    /** @type {NodeListOf<Element>} */
    var allSlides = originals;
    /**
     * Loop: DOM is [clone×n][original×n][clone×n]; stripIndex 0..3n-1.
     * Stable band: n..3n-1 (originals + right clones). We only normalize when
     * stripIndex < n (left clone zone) so moving onto the right duplicate never
     * auto-snaps back to the middle copy of slide 1.
     */
    var stripIndex = loop ? n : 0;
    /** stripIndex at pointerdown; used to detect wrap-forward from last clone */
    var pointerDownStripIndex = 0;
    var activePointerId = null;
    var startClientX = 0;
    var startClientY = 0;
    var dragStartTranslate = 0;
    var moveSamples = [];
    var didDrag = false;
    var pointerDownOnLipscore = false;
    var dragThreshold = 10;
    var autoplayTimer = null;
    var resumeTimer = null;

    var reducedMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var autoplayEnabled = bar.getAttribute('data-autoplay') === 'true' && n > 1 && !reducedMotion;
    var autoplayDelay = parseInt(bar.getAttribute('data-autoplay-delay') || '5000', 10);
    if (autoplayDelay < 2000) autoplayDelay = 2000;

    function rtlSign() {
      return getComputedStyle(viewport).direction === 'rtl' ? -1 : 1;
    }

    function setInstant(on) {
      track.classList.toggle('header__top-bar-track--instant', !!on);
    }

    function flushReflow() {
      void track.offsetWidth;
    }

    function cloneSlide(el) {
      var c = el.cloneNode(true);
      c.classList.add('header__top-bar-slide--clone');
      c.setAttribute('tabindex', '-1');
      c.removeAttribute('id');
      c.querySelectorAll('[id]').forEach(function (node) {
        node.removeAttribute('id');
      });
      return c;
    }

    if (loop) {
      for (var pre = n - 1; pre >= 0; pre--) {
        track.insertBefore(cloneSlide(originals[pre]), track.firstChild);
      }
      for (var post = 0; post < n; post++) {
        track.appendChild(cloneSlide(originals[post]));
      }
      allSlides = track.querySelectorAll('.header__top-bar-slide');
    }

    function translateForStrip(si) {
      return -si * slideWidth;
    }

    /** Fold translate into the physical strip so drag can cross the loop in one motion (period = n slides). */
    function wrapLoopTranslate(tx) {
      if (!loop || !slideWidth) return tx;
      var w = slideWidth;
      var lo = -(3 * n - 1) * w;
      var hi = 0;
      var period = n * w;
      var guard = 0;
      while (tx < lo && guard++ < 500) tx += period;
      guard = 0;
      while (tx > hi && guard++ < 500) tx -= period;
      return tx;
    }

    function syncSlideWidth() {
      slideWidth = viewport.clientWidth;
      if (!slideWidth) return;
      viewport.style.setProperty('--header-top-bar-slide-px', slideWidth + 'px');
      setInstant(true);
      track.style.transform = 'translate3d(' + translateForStrip(stripIndex) + 'px, 0, 0)';
      setInstant(false);
    }

    function applyTranslate(px, animate) {
      if (animate) setInstant(false);
      else setInstant(true);
      track.style.transform = 'translate3d(' + px + 'px, 0, 0)';
      if (!animate) {
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            setInstant(false);
          });
        });
      }
    }

    function updateAria() {
      allSlides.forEach(function (slide, si) {
        slide.setAttribute('aria-hidden', si === stripIndex ? 'false' : 'true');
      });
    }

    function normalizeStripInstant() {
      if (!loop || !slideWidth) return;
      if (stripIndex >= n) return;
      setInstant(true);
      flushReflow();
      while (stripIndex < n) stripIndex += n;
      track.style.transform = 'translate3d(' + translateForStrip(stripIndex) + 'px, 0, 0)';
      flushReflow();
      updateAria();
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (!track.isConnected) return;
          setInstant(false);
        });
      });
    }

    function onTrackTransitionEnd(e) {
      if (e.target !== track || e.propertyName !== 'transform') return;
      requestAnimationFrame(function () {
        normalizeStripInstant();
      });
    }

    function goToStrip(si, animate) {
      if (!slideWidth) syncSlideWidth();
      if (loop) {
        stripIndex = Math.max(0, Math.min(3 * n - 1, si));
      } else {
        stripIndex = Math.max(0, Math.min(n - 1, si));
      }
      var useAnim = animate !== false && !reducedMotion;
      applyTranslate(translateForStrip(stripIndex), useAnim);
      updateAria();
      if (loop && !useAnim && stripIndex < n) {
        normalizeStripInstant();
      }
    }

    /** @param {number} logical 0..n-1 */
    function goTo(logical, animate) {
      if (n < 1) return;
      var li = ((logical % n) + n) % n;
      if (loop) goToStrip(n + li, animate);
      else goToStrip(li, animate);
    }

    /**
     * Forward one slide in loop mode. From the last cell (3n-1), invisibly align to
     * the same visual as strip 2n-1 then animate to 2n (first slide duplicate).
     */
    function loopStepForward(animate) {
      if (stripIndex === 3 * n - 1) {
        var useAnim = animate !== false && !reducedMotion;
        setInstant(true);
        flushReflow();
        if (!useAnim) {
          stripIndex = 2 * n;
          track.style.transform = 'translate3d(' + translateForStrip(stripIndex) + 'px, 0, 0)';
          flushReflow();
          updateAria();
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              if (!track.isConnected) return;
              setInstant(false);
            });
          });
          return;
        }
        stripIndex = 2 * n - 1;
        track.style.transform = 'translate3d(' + translateForStrip(stripIndex) + 'px, 0, 0)';
        flushReflow();
        updateAria();
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            if (!track.isConnected) return;
            setInstant(false);
            flushReflow();
            goToStrip(2 * n, true);
          });
        });
        return;
      }
      goToStrip(stripIndex + 1, animate);
    }

    function loopStepBy(delta, animate) {
      if (delta > 0) loopStepForward(animate);
      else goToStrip(stripIndex - 1, animate);
    }

    function stepNext() {
      if (loop) loopStepForward(true);
      else goToStrip(Math.min(n - 1, stripIndex + 1), true);
    }

    function stepPrev() {
      if (loop) goToStrip(stripIndex - 1, true);
      else goToStrip(Math.max(0, stripIndex - 1), true);
    }

    function clearResumeTimer() {
      if (resumeTimer) {
        clearTimeout(resumeTimer);
        resumeTimer = null;
      }
    }

    function stopAutoplayInterval() {
      if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    }

    function startAutoplay() {
      clearResumeTimer();
      stopAutoplayInterval();
      if (!autoplayEnabled || n < 2) return;
      autoplayTimer = window.setInterval(stepNext, autoplayDelay);
    }

    function pauseAutoplayThenResume() {
      stopAutoplayInterval();
      clearResumeTimer();
      resumeTimer = window.setTimeout(startAutoplay, Math.max(autoplayDelay, 2500));
    }

    function pushSample(x) {
      var t = Date.now();
      moveSamples.push({ t: t, x: x });
      while (moveSamples.length > 6) moveSamples.shift();
    }

    function endVelocitySign() {
      if (moveSamples.length < 2) return 0;
      var a = moveSamples[0];
      var b = moveSamples[moveSamples.length - 1];
      var dt = b.t - a.t;
      if (dt < 1) return 0;
      var vx = (b.x - a.x) / dt;
      if (vx < -0.45) return -1;
      if (vx > 0.45) return 1;
      return 0;
    }

    function isLipscoreTarget(el) {
      return !!(el && el.closest && el.closest('[data-mm-lipscore-service], .header__top-bar-lipscore-wrap'));
    }

    function lipscoreFromPoint(x, y) {
      var el = document.elementFromPoint(x, y);
      return isLipscoreTarget(el);
    }

    function openLipscoreIfTap(e) {
      if (didDrag) return;
      var x = e && typeof e.clientX === 'number' ? e.clientX : startClientX;
      var y = e && typeof e.clientY === 'number' ? e.clientY : startClientY;
      if (!pointerDownOnLipscore && !lipscoreFromPoint(x, y)) return;
      if (typeof window.mmOpenLipscoreServiceReviews === 'function') {
        window.mmOpenLipscoreServiceReviews();
      }
    }

    function attachWindowPointer() {
      window.addEventListener('pointermove', onPointerMove, { passive: false });
      window.addEventListener('pointerup', onPointerUp);
      window.addEventListener('pointercancel', onPointerUp);
    }

    function detachWindowPointer() {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    }

    function beginDrag(e) {
      if (didDrag) return;
      didDrag = true;
      viewport.classList.add('is-dragging');
      setInstant(true);
      try {
        viewport.setPointerCapture(e.pointerId);
      } catch (err) {}
    }

    function onPointerDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      stopAutoplayInterval();
      clearResumeTimer();
      syncSlideWidth();
      activePointerId = e.pointerId;
      startClientX = e.clientX;
      startClientY = e.clientY;
      didDrag = false;
      pointerDownOnLipscore = isLipscoreTarget(e.target);
      pointerDownStripIndex = stripIndex;
      dragStartTranslate = translateForStrip(stripIndex);
      moveSamples = [{ t: Date.now(), x: e.clientX }];
      attachWindowPointer();
    }

    function onPointerMove(e) {
      if (activePointerId !== e.pointerId) return;
      var dx = e.clientX - startClientX;
      var dy = e.clientY - startClientY;
      if (!didDrag) {
        if (Math.abs(dx) < dragThreshold && Math.abs(dy) < dragThreshold) return;
        if (Math.abs(dy) > Math.abs(dx)) return;
        beginDrag(e);
      }
      if (e.cancelable) e.preventDefault();
      pushSample(e.clientX);
      var dragDelta = (e.clientX - startClientX) * rtlSign();
      var tx = dragStartTranslate + dragDelta;
      var minT;
      var maxT;
      if (loop) {
        tx = wrapLoopTranslate(tx);
      } else {
        minT = -(n - 1) * slideWidth;
        maxT = 0;
        tx = Math.min(maxT, Math.max(minT, tx));
      }
      track.style.transform = 'translate3d(' + tx + 'px, 0, 0)';
    }

    function onPointerUp(e) {
      if (activePointerId === null) return;
      if (e && e.pointerId != null && e.pointerId !== activePointerId) return;
      var endX = e && typeof e.clientX === 'number' ? e.clientX : startClientX;
      var pointerId = activePointerId;
      var dragged = didDrag;
      activePointerId = null;
      didDrag = false;
      viewport.classList.remove('is-dragging');
      detachWindowPointer();
      if (pointerId != null) {
        try {
          viewport.releasePointerCapture(pointerId);
        } catch (err2) {}
      }

      if (!dragged) {
        openLipscoreIfTap(e);
        pauseAutoplayThenResume();
        return;
      }

      var dragDelta = slideWidth ? (endX - startClientX) * rtlSign() : 0;
      var rawTx = dragStartTranslate + dragDelta;
      var endTx;
      if (loop && slideWidth) {
        endTx = wrapLoopTranslate(rawTx);
      } else if (slideWidth) {
        var minEnd = -(n - 1) * slideWidth;
        var maxEnd = 0;
        endTx = Math.min(maxEnd, Math.max(minEnd, rawTx));
      } else {
        endTx = translateForStrip(stripIndex);
      }
      var maxStrip = loop ? 3 * n - 1 : n - 1;
      var frac = slideWidth ? -endTx / slideWidth : stripIndex;
      var target = Math.round(frac);
      if (target < 0) target = 0;
      if (target > maxStrip) target = maxStrip;
      var flick = endVelocitySign();
      if (flick < 0) target += 1;
      if (flick > 0) target -= 1;
      if (target < 0) target = 0;

      setInstant(false);
      flushReflow();

      if (loop && pointerDownStripIndex === 3 * n - 1 && target > maxStrip) {
        loopStepForward(true);
        pauseAutoplayThenResume();
        return;
      }
      if (target > maxStrip) target = maxStrip;

      goToStrip(target, true);
      pauseAutoplayThenResume();
    }

    function onLostPointerCapture() {
      if (activePointerId === null) return;
      onPointerUp({ pointerId: activePointerId, clientX: startClientX });
    }

    function onKeyDown(e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (loop) loopStepBy(-rtlSign(), true);
        else goTo(stripIndex - rtlSign(), true);
        pauseAutoplayThenResume();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (loop) loopStepBy(rtlSign(), true);
        else goTo(stripIndex + rtlSign(), true);
        pauseAutoplayThenResume();
      }
    }

    originals.forEach(function (s) {
      s.setAttribute('tabindex', '-1');
    });

    window.addEventListener('resize', syncSlideWidth, { passive: true });
    var ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(syncSlideWidth);
      ro.observe(viewport);
    }

    if (loop) track.addEventListener('transitionend', onTrackTransitionEnd);

    viewport.addEventListener('pointerdown', onPointerDown);
    viewport.addEventListener('lostpointercapture', onLostPointerCapture);
    viewport.addEventListener('keydown', onKeyDown);

    bar.addEventListener('mouseenter', stopAutoplayInterval);
    bar.addEventListener('mouseleave', startAutoplay);
    bar.addEventListener('focusin', stopAutoplayInterval);
    bar.addEventListener('focusout', function () {
      clearResumeTimer();
      resumeTimer = window.setTimeout(startAutoplay, 900);
    });

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        syncSlideWidth();
        if (loop) {
          stripIndex = n;
          setInstant(true);
          track.style.transform = 'translate3d(' + translateForStrip(n) + 'px, 0, 0)';
          setInstant(false);
          updateAria();
        } else {
          stripIndex = 0;
          goTo(0, false);
        }
        if (typeof window.mmLoadLipscoreServiceBadge === 'function') {
          window.mmLoadLipscoreServiceBadge();
        }
        startAutoplay();
      });
    });
  }

  function initDualLogoSwap(header, root) {
    if (header.dataset.headerLogoSwapInit === 'true') return;

    var swapContainer = header.querySelector('.header__logo-scroll-swap');
    if (!swapContainer) return;

    var topImg = swapContainer.querySelector('.header__logo-image--page-top-scroll');
    var scrolledImg = swapContainer.querySelector('.header__logo-image--scrolled');
    var transparentImg = swapContainer.querySelector('.header__logo-image--transparent');
    var solidImg = swapContainer.querySelector('.header__logo-image--solid');

    if (!topImg && !scrolledImg && !solidImg && !transparentImg) return;

    header.dataset.headerLogoSwapInit = 'true';

    var threshold = 2;

    function transparentHeaderActive() {
      var raw = getComputedStyle(header).getPropertyValue('--header-show-transparent-logo').trim();
      return raw === '1' || raw === '1.0';
    }

    function setImageVisibility(img, visible) {
      if (!img) return;
      img.style.display = visible ? 'block' : 'none';
      if (visible) {
        img.removeAttribute('aria-hidden');
      } else {
        img.setAttribute('aria-hidden', 'true');
      }
    }

    function syncPageTopLogo() {
      var atTop = Math.max(0, window.scrollY || document.documentElement.scrollTop) <= threshold;
      var transparentOn = transparentHeaderActive();

      if (transparentOn && transparentImg) {
        setImageVisibility(transparentImg, true);
        setImageVisibility(topImg, false);
        setImageVisibility(scrolledImg, false);
        setImageVisibility(solidImg, false);
        return;
      }

      setImageVisibility(transparentImg, false);

      if (topImg && scrolledImg) {
        if (atTop) {
          setImageVisibility(topImg, true);
          setImageVisibility(scrolledImg, false);
        } else {
          setImageVisibility(topImg, false);
          setImageVisibility(scrolledImg, true);
        }
        return;
      }

      if (topImg) {
        setImageVisibility(topImg, true);
        setImageVisibility(scrolledImg, false);
        return;
      }

      if (scrolledImg) {
        setImageVisibility(scrolledImg, true);
        return;
      }

      setImageVisibility(solidImg, true);
    }

    var rafScheduled = false;
    function syncPageTopLogoRaf() {
      if (rafScheduled) return;
      rafScheduled = true;
      requestAnimationFrame(function () {
        rafScheduled = false;
        syncPageTopLogo();
      });
    }

    var ac = new AbortController();
    var signal = ac.signal;

    function onSectionUnload(event) {
      if (!event.target || event.target !== root) return;
      mo.disconnect();
      ac.abort();
      document.removeEventListener('shopify:section:unload', onSectionUnload);
    }

    syncPageTopLogo();
    window.addEventListener('scroll', syncPageTopLogo, { passive: true, signal: signal });
    window.addEventListener('resize', syncPageTopLogoRaf, { passive: true, signal: signal });
    header.addEventListener('mouseenter', syncPageTopLogoRaf, { signal: signal });
    header.addEventListener('mouseleave', syncPageTopLogoRaf, { signal: signal });

    var mo = new MutationObserver(syncPageTopLogoRaf);
    mo.observe(header, { subtree: true, attributes: true, attributeFilter: ['open', 'class'] });

    document.addEventListener(
      'shopify:section:load',
      function (event) {
        if (event.target && event.target === root) syncPageTopLogo();
      },
      { signal: signal }
    );

    document.addEventListener('shopify:section:unload', onSectionUnload);
  }

  function initHeaderDesktopSearch(root) {
    var wrap = root.querySelector('[data-header-search-desktop]');
    if (!wrap || wrap.hasAttribute('data-desktop-search-initialized')) return;
    wrap.setAttribute('data-desktop-search-initialized', '');

    var input = wrap.querySelector('.header__search-desktop-input');
    var panel = wrap.querySelector('[data-desktop-search-panel]');
    var closeBtn = wrap.querySelector('[data-desktop-search-close]');
    if (!input || !panel || !closeBtn) return;

    var panelOpen = false;
    /** @type {((e: KeyboardEvent) => void) | null} */
    var onKey = null;

    function syncQueryClass() {
      var has = (input.value || '').trim().length > 0;
      panel.classList.toggle('header__search-desktop-panel--has-query', has);
    }

    function removeDocListeners() {
      if (onKey) {
        document.removeEventListener('keydown', onKey, true);
        onKey = null;
      }
    }

    function closePanel() {
      if (!panelOpen) return;
      panelOpen = false;
      panel.hidden = true;
      document.documentElement.style.overflow = '';
      removeDocListeners();
      input.blur();
    }

    function openPanel() {
      if (panelOpen) return;
      panelOpen = true;
      panel.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      syncQueryClass();

      onKey = function (e) {
        if (e.key === 'Escape') {
          closePanel();
        }
      };
      document.addEventListener('keydown', onKey, true);
    }

    input.addEventListener('focus', openPanel);
    input.addEventListener('input', syncQueryClass);

    closeBtn.addEventListener('click', function () {
      closePanel();
    });

    document.addEventListener(
      'shopify:section:unload',
      function (e) {
        if (e.target === root) {
          closePanel();
          wrap.removeAttribute('data-desktop-search-initialized');
        }
      },
      { passive: true }
    );
  }

  function initForRoot(root) {
    if (!root || !root.classList.contains('shopify-section--header')) return;
    setHeaderHeight(root);
    initHeaderTopBarCarousel(root);
    initHeaderDesktopSearch(root);
    var header = root.querySelector('x-header');
    if (header && header.querySelector('.header__logo-scroll-swap')) initDualLogoSwap(header, root);
  }

  function init() {
    var root = document.querySelector('.shopify-section--header');
    initForRoot(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('resize', function () {
    var root = document.querySelector('.shopify-section--header');
    setHeaderHeight(root);
  });

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.classList && event.target.classList.contains('shopify-section--header')) {
      initForRoot(event.target);
    }
  });
})();
